import React, { useState, useEffect } from "react";
import { NumericFormat } from "react-number-format";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import currencies from "../../../constants/CurrencyData";

interface AmountInputFieldProps {
  value?: string | number;
  onChange?: (value: string, error: boolean) => void;
  onFinish?: (value: string, error: boolean) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  min?: number;
  max?: number;
  isPercentage?: boolean;
  showError?: boolean;
  variant?: "default" | "inline";
}

const AmountInputField: React.FC<AmountInputFieldProps> = ({
  value = "",
  onChange = () => {},
  onFinish = () => {},
  placeholder = "",
  disabled = false,
  error = false,
  min,
  max,
  isPercentage = false,
  showError = false,
  variant = "default",
}) => {
  const { t } = useTranslation();
  const projectCurrency = useSelector((state: any) =>
    state.projectReducer.getIn(["project", "currency"])
  );

  const currency = projectCurrency || "USD";
  const currencyData = currencies[currency] || currencies["USD"];
  const decimalScale = isPercentage ? 0 : currencyData.decimal_digits || 2;

  const [inputValue, setInputValue] = useState(value);

  const validateValue = (val: any) => {
    let hasError = false;
    if (val !== undefined && (min !== undefined || max !== undefined)) {
      if ((min !== undefined && val < min) || (max !== undefined && val > max)) {
        hasError = true;
      }
    }
    return hasError;
  };

  const handleValueChange = (values: any) => {
    const { value: rawValue, floatValue } = values;
    setInputValue(rawValue);

    const hasError = validateValue(floatValue);
    onChange(rawValue, hasError);
  };

  const handleFinish = () => {
    if (inputValue !== value) {
      const stringValue = String(inputValue || "0");
      const numericValue = parseFloat(stringValue);
      const hasError = validateValue(numericValue);
      onFinish(stringValue, hasError);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleFinish();
    }
  };

  const handleFocus = () => {
    if (inputValue) {
      const numericValue = parseFloat(String(inputValue));
      const hasError = validateValue(numericValue);
      onChange(String(inputValue), hasError);
    }
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const isInline = variant === "inline";
  const containerClass = isInline
    ? "flex items-center w-full rounded-md border border-gray-300 px-2 py-1 min-h-[28px] focus-within:border-blue-500"
    : "w-full";

  const inputClass = isInline
    ? `block w-full bg-white text-[12px] leading-4 font-medium text-gray-800 placeholder:text-gray-400 focus:outline-none border-0 p-0 ${
        error ? "text-red-800" : ""
      }`
    : `block w-full h-9 border p-1.5 text-sm shadow-xs border-gray-300 placeholder:text-gray-400 focus:border-blue-500 rounded ${
        error ? "border-red-500 focus:border-red-500" : ""
      }`;

  return (
    <div className={containerClass}>
      <NumericFormat
        disabled={disabled}
        value={inputValue}
        onValueChange={handleValueChange}
        onFocus={handleFocus}
        onBlur={handleFinish}
        onKeyDown={handleKeyDown}
        thousandSeparator={isPercentage ? false : ","}
        decimalSeparator="."
        decimalScale={decimalScale}
        fixedDecimalScale={false}
        allowNegative={false}
        placeholder={placeholder}
        className={inputClass}
      />
      {error && showError && (min !== undefined || max !== undefined) && (
        <p className="mt-1 text-sm text-red-600">
          {t("Value must be between {{min}} and {{max}}", {
            min: min ?? 0,
            max: max ?? "∞",
          })}
        </p>
      )}
    </div>
  );
};

export default AmountInputField;
